import React, { useEffect, useRef, useState } from "react";
import Button from "./Button";
import { TextArea } from "./Field";
import { CheckIcon } from "./icons";

/**
 * Nudge card (E §2 — the centerpiece of the referral home). A question,
 * never a task: fawn left-border card, initials chip, three one-tap
 * actions, inline note expansion, answered state with a 10s Undo.
 *
 * Undo is CLIENT-SIDE DELAYED COMMIT (E §9.1's blessed v1): the respond
 * call fires only after the 10s undo window closes (or immediately if the
 * card unmounts first, so navigating away never loses an answer). Undo
 * within the window cancels the commit — nothing was sent yet. A commit
 * failure resurrects the card and surfaces `commitError` via onError.
 *
 * props:
 *   nudge:     { _id, headline, context, initials }
 *   onCommit:  (status, note) => Promise   — the actual API call
 *   onError:   (message) => void           — toast hookup
 *   highlight: boolean                     — deep-link pulse (§2)
 *   receipt:   string | null               — pre-answered deep-link receipt
 */
const UNDO_SECONDS = 10;

const ANSWERED_COPY = {
  reach_out: (first) => `We'll reach out to ${first} — thank you!`,
  reach_out_note: () => "Sent with your note — thank you!",
  hold_off: () => "Got it — we'll check back next year."
};

function NudgeCard({ nudge, onCommit, onError, highlight = false, receipt = null }) {
  const [mode, setMode] = useState(receipt ? "receipt" : "idle"); // idle | notes | answered | receipt | gone
  const [note, setNote] = useState("");
  const [answer, setAnswer] = useState(null); // { status, note, copyKey }
  const commitTimer = useRef(null);
  const committed = useRef(false);
  const ref = useRef(null);

  const firstName = (nudge.prospectName || nudge.headline || "").split(" ")[0] || "them";

  const commit = (status, noteText) => {
    if (committed.current) {
      return;
    }
    committed.current = true;
    onCommit(status, noteText).catch(() => {
      // Resurrect the card — a silent eat is the legacy bug class.
      committed.current = false;
      setMode("idle");
      setAnswer(null);
      if (onError) {
        onError("That didn't save — try again.");
      }
    });
  };

  const answerWith = (status, noteText = "") => {
    const copyKey = status === "reach_out" && noteText ? "reach_out_note" : status;
    setAnswer({ status, note: noteText, copyKey });
    setMode("answered");
    commitTimer.current = window.setTimeout(() => {
      commit(status, noteText);
      setMode("gone");
    }, UNDO_SECONDS * 1000);
  };

  const undo = () => {
    window.clearTimeout(commitTimer.current);
    setAnswer(null);
    setNote("");
    setMode("idle");
  };

  // Unmount while an answer is inside the undo window → commit immediately
  // (navigating away must never lose an answer).
  const pendingAnswer = useRef(null);
  pendingAnswer.current = mode === "answered" ? answer : null;
  useEffect(() => {
    return () => {
      window.clearTimeout(commitTimer.current);
      if (pendingAnswer.current) {
        commit(pendingAnswer.current.status, pendingAnswer.current.note);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deep-link pulse (§2): soft fawn tint for 1.5s.
  useEffect(() => {
    if (highlight && ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
      ref.current.classList.add("fk-nudge--pulse");
      const t = window.setTimeout(
        () => ref.current && ref.current.classList.remove("fk-nudge--pulse"),
        1500
      );
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [highlight]);

  if (mode === "gone") {
    return null;
  }

  if (mode === "receipt") {
    return (
      <div className="fk-nudge fk-nudge--answered" ref={ref}>
        <span className="fk-nudge__check">
          <CheckIcon size={14} />
        </span>
        <span>{receipt}</span>
      </div>
    );
  }

  if (mode === "answered") {
    return (
      <div className="fk-nudge fk-nudge--answered" ref={ref}>
        <span className="fk-nudge__check">
          <CheckIcon size={14} />
        </span>
        <span>{ANSWERED_COPY[answer.copyKey](firstName)}</span>
        <button type="button" className="fk-nudge__undo" onClick={undo}>
          Undo
        </button>
      </div>
    );
  }

  return (
    <div className="fk-nudge" ref={ref}>
      <div className="fk-nudge__row">
        <span className="fk-nudge__chip">{nudge.initials}</span>
        <div className="fk-nudge__body">
          <p className="fk-nudge__headline">{nudge.headline}</p>
          {nudge.context ? <p className="fk-nudge__context">{nudge.context}</p> : null}
        </div>
      </div>

      {mode === "notes" ? (
        <div className="fk-nudge__notes">
          <TextArea
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder='Anything Evan and Randy should know? e.g. "She mentioned buying a new car."'
            aria-label="Note for the writing agents"
          />
          <div className="fk-nudge__actions">
            <Button variant="primary" onClick={() => answerWith("reach_out", note.trim())}>
              Send to Evan &amp; Randy
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setNote("");
                setMode("idle");
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="fk-nudge__actions">
          <Button variant="primary" onClick={() => answerWith("reach_out")}>
            Yes, reach out
          </Button>
          <Button variant="secondary" onClick={() => setMode("notes")}>
            Yes, with notes
          </Button>
          <Button variant="ghost" onClick={() => answerWith("hold_off")}>
            No, hold off
          </Button>
        </div>
      )}
    </div>
  );
}

export default NudgeCard;
